import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { 
  Users, ArrowLeft, Search, Filter, CheckCircle, XCircle, 
  AlertTriangle, Loader2, RefreshCw, Eye, UserCheck, UserX,
  TrendingUp, DollarSign, Clock, Award, ChevronDown, Tag, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDealersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [topDealers, setTopDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Custom rate dialog states
  const [customRateDialogOpen, setCustomRateDialogOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [customRateValue, setCustomRateValue] = useState('');
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'application' or 'dealer'
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === 'applications') {
        const response = await axios.get(`${API}/api/admin/dealer-applications?status_filter=pending`, { headers });
        setApplications(response.data);
      } else if (activeTab === 'active') {
        const response = await axios.get(`${API}/api/admin/dealers?status_filter=approved`, { headers });
        setDealers(response.data);
      } else if (activeTab === 'at_risk') {
        const response = await axios.get(`${API}/api/admin/dealers`, { headers });
        setDealers(response.data.filter(d => d.dealer_warning_count > 0 && d.dealer_status === 'approved'));
      } else if (activeTab === 'inactive') {
        const response = await axios.get(`${API}/api/admin/dealers?status_filter=inactive`, { headers });
        setDealers(response.data);
      } else if (activeTab === 'rejected') {
        const response = await axios.get(`${API}/api/admin/dealer-applications?status_filter=rejected`, { headers });
        setApplications(response.data);
      } else if (activeTab === 'top') {
        const [monthlyRes, allTimeRes] = await Promise.all([
          axios.get(`${API}/api/admin/dealers/top?period=month&limit=10`, { headers }),
          axios.get(`${API}/api/admin/dealers/top?period=all_time&limit=10`, { headers })
        ]);
        setTopDealers({ monthly: monthlyRes.data, allTime: allTimeRes.data });
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/admin/dealers/${userId}/approve`, {}, { headers });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve dealer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/dealers/${selectedApplication.user_id}/reject`,
        { reason: rejectReason },
        { headers }
      );
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedApplication(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this dealer?')) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/admin/dealers/${userId}/deactivate`, {}, { headers });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to deactivate dealer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/admin/dealers/${userId}/reactivate`, {}, { headers });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reactivate dealer');
    } finally {
      setActionLoading(false);
    }
  };

  const openCustomRateDialog = (dealer) => {
    setSelectedDealer(dealer);
    setCustomRateValue(dealer.dealer_rate ? dealer.dealer_rate.toString() : '');
    setCustomRateDialogOpen(true);
  };

  const handleSetCustomRate = async () => {
    if (!selectedDealer) return;
    
    setActionLoading(true);
    try {
      const rateValue = customRateValue.trim() === '' ? null : parseFloat(customRateValue);
      
      // Validate rate
      if (rateValue !== null && (isNaN(rateValue) || rateValue <= 0 || rateValue > 50)) {
        alert('Please enter a valid rate between $0.01 and $50.00');
        setActionLoading(false);
        return;
      }
      
      await axios.post(
        `${API}/api/admin/dealers/${selectedDealer.id}/set-custom-rate`,
        { custom_rate: rateValue },
        { headers }
      );
      
      setCustomRateDialogOpen(false);
      setSelectedDealer(null);
      setCustomRateValue('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to set custom rate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearCustomRate = async () => {
    if (!selectedDealer) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/dealers/${selectedDealer.id}/set-custom-rate`,
        { custom_rate: null },
        { headers }
      );
      
      setCustomRateDialogOpen(false);
      setSelectedDealer(null);
      setCustomRateValue('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to clear custom rate');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete || deleteConfirmText !== 'DELETE') return;
    
    setActionLoading(true);
    try {
      if (deleteType === 'application') {
        await axios.delete(`${API}/api/admin/dealer-applications/${itemToDelete.id}`, { headers });
      }
      // Note: For dealers (users), we don't delete the user account, just reset their dealer status
      // This could be expanded to fully remove dealer status if needed
      
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDeleteType(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-800">Pending</Badge>;
      case 'inactive':
        return <Badge className="bg-amber-100 text-amber-800">Inactive</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dealer Management</h1>
              <p className="text-gray-500 text-sm">Manage dealer applications and accounts</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-6 w-full max-w-3xl">
            <TabsTrigger value="applications" className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-1">
              <UserCheck className="h-4 w-4" />
              Active
            </TabsTrigger>
            <TabsTrigger value="at_risk" className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              At Risk
            </TabsTrigger>
            <TabsTrigger value="inactive" className="flex items-center gap-1">
              <UserX className="h-4 w-4" />
              Inactive
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-1">
              <XCircle className="h-4 w-4" />
              Rejected
            </TabsTrigger>
            <TabsTrigger value="top" className="flex items-center gap-1">
              <Award className="h-4 w-4" />
              Top Dealers
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : applications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No pending applications
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <Card key={app.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{app.name}</h3>
                            {getStatusBadge(app.status)}
                          </div>
                          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Email:</span> {app.email}
                            </div>
                            <div>
                              <span className="font-medium">Phone:</span> {app.phone}
                            </div>
                            <div>
                              <span className="font-medium">Applied:</span> {formatDate(app.created_at)}
                            </div>
                          </div>
                          <div className="mt-3 text-sm">
                            <span className="font-medium">Products:</span>{' '}
                            {app.products_sold?.join(', ')}
                            {app.products_sold_other && ` (${app.products_sold_other})`}
                          </div>
                          <div className="mt-1 text-sm">
                            <span className="font-medium">Sales Channels:</span>{' '}
                            {app.sales_channels?.join(', ')}
                            {app.sales_channels_other && ` (${app.sales_channels_other})`}
                          </div>
                          <div className="mt-3 text-sm bg-gray-50 p-3 rounded">
                            <span className="font-medium">Business Goals:</span><br />
                            {app.business_goals}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button 
                            onClick={() => handleApprove(app.user_id)}
                            className="bg-green-600 hover:bg-green-700"
                            disabled={actionLoading}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedApplication(app);
                              setRejectDialogOpen(true);
                            }}
                            disabled={actionLoading}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button 
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => openDeleteDialog(app, 'application')}
                            disabled={actionLoading}
                            data-testid={`delete-application-${app.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Active/At Risk/Inactive Dealers Tab */}
          {['active', 'at_risk', 'inactive'].map((tabValue) => (
            <TabsContent key={tabValue} value={tabValue}>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : dealers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    No {tabValue === 'at_risk' ? 'at-risk' : tabValue} dealers
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {dealers.map((dealer) => (
                    <Card key={dealer.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="font-semibold text-lg">{dealer.name}</h3>
                              {getStatusBadge(dealer.dealer_status)}
                              {dealer.dealer_tier && (
                                <Badge variant="outline" className="capitalize">{dealer.dealer_tier} Tier</Badge>
                              )}
                              {dealer.dealer_rate && (
                                <Badge className="bg-purple-100 text-purple-800" data-testid={`custom-rate-badge-${dealer.id}`}>
                                  <Tag className="h-3 w-3 mr-1" />
                                  Custom: ${dealer.dealer_rate.toFixed(2)}/card
                                </Badge>
                              )}
                              {dealer.dealer_warning_count > 0 && (
                                <Badge className="bg-amber-100 text-amber-800">
                                  {dealer.dealer_warning_count} Warning{dealer.dealer_warning_count > 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <div className="grid md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Email</span>
                                <div className="font-medium">{dealer.email}</div>
                              </div>
                              <div>
                                <span className="text-gray-500">This Month</span>
                                <div className="font-medium">
                                  {dealer.monthly_card_count} / {dealer.monthly_minimum} cards
                                </div>
                              </div>
                              <div>
                                <span className="text-gray-500">All-Time</span>
                                <div className="font-medium">{dealer.all_time_cards?.toLocaleString()} cards</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Total Revenue</span>
                                <div className="font-medium">{formatCurrency(dealer.all_time_revenue)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid={`dealer-actions-${dealer.id}`}>
                                  Actions <ChevronDown className="h-4 w-4 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {dealer.dealer_status === 'approved' && (
                                  <>
                                    <DropdownMenuItem 
                                      onClick={() => openCustomRateDialog(dealer)}
                                      data-testid={`set-custom-rate-${dealer.id}`}
                                    >
                                      <DollarSign className="h-4 w-4 mr-2" />
                                      {dealer.dealer_rate ? 'Edit Custom Rate' : 'Set Custom Rate'}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => handleDeactivate(dealer.id)}
                                      className="text-red-600"
                                    >
                                      <UserX className="h-4 w-4 mr-2" />
                                      Deactivate
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {dealer.dealer_status === 'inactive' && (
                                  <DropdownMenuItem onClick={() => handleReactivate(dealer.id)}>
                                    <UserCheck className="h-4 w-4 mr-2" />
                                    Reactivate
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}

          {/* Rejected Tab */}
          <TabsContent value="rejected">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : applications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No rejected applications
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <Card key={app.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{app.name}</h3>
                            {getStatusBadge(app.status)}
                          </div>
                          <div className="text-sm text-gray-600">
                            <p><span className="font-medium">Email:</span> {app.email}</p>
                            <p><span className="font-medium">Rejected:</span> {formatDate(app.reviewed_at)}</p>
                            <p><span className="font-medium">Reason:</span> {app.rejection_reason}</p>
                            {app.can_reapply_at && (
                              <p><span className="font-medium">Can Reapply:</span> {formatDate(app.can_reapply_at)}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Top Dealers Tab */}
          <TabsContent value="top">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Monthly Top */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                      Top Dealers (This Month)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {topDealers?.monthly?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {topDealers?.monthly?.map((dealer, index) => (
                          <div key={dealer.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                            <div className="flex items-center">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                                index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                index === 1 ? 'bg-gray-300 text-gray-700' :
                                index === 2 ? 'bg-amber-600 text-white' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {index + 1}
                              </span>
                              <div>
                                <div className="font-medium">{dealer.name}</div>
                                <div className="text-sm text-gray-500">{dealer.email}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">{dealer.total_cards?.toLocaleString()} cards</div>
                              <div className="text-sm text-gray-500">{formatCurrency(dealer.total_revenue)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* All-Time Top */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Award className="h-5 w-5 mr-2 text-purple-600" />
                      Top Dealers (All-Time)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {topDealers?.allTime?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No data yet</p>
                    ) : (
                      <div className="space-y-3">
                        {topDealers?.allTime?.map((dealer, index) => (
                          <div key={dealer.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                            <div className="flex items-center">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                                index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                index === 1 ? 'bg-gray-300 text-gray-700' :
                                index === 2 ? 'bg-amber-600 text-white' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {index + 1}
                              </span>
                              <div>
                                <div className="font-medium">{dealer.name}</div>
                                <div className="text-sm text-gray-500">{dealer.email}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">{dealer.total_cards?.toLocaleString()} cards</div>
                              <div className="text-sm text-gray-500">{formatCurrency(dealer.total_revenue)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              They will be able to reapply after 90 days.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rejectReason">Rejection Reason</Label>
            <Textarea
              id="rejectReason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReject} 
              disabled={actionLoading || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Rate Dialog */}
      <Dialog open={customRateDialogOpen} onOpenChange={setCustomRateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Custom Rate</DialogTitle>
            <DialogDescription>
              Set a custom per-card rate for <span className="font-semibold">{selectedDealer?.name}</span>.
              This rate will override standard tier-based pricing.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="customRate">Custom Rate (per card)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="customRate"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="50"
                  value={customRateValue}
                  onChange={(e) => setCustomRateValue(e.target.value)}
                  placeholder="e.g., 7.50"
                  className="pl-7"
                  data-testid="custom-rate-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter a value between $0.01 and $50.00, or leave blank to use tier pricing
              </p>
            </div>
            
            {selectedDealer?.dealer_rate && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  Current custom rate: <span className="font-bold">${selectedDealer.dealer_rate.toFixed(2)}/card</span>
                </p>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> When a custom rate is set, the dealer will be charged this rate 
                for all submissions regardless of quantity. Standard tier benefits like Minimum Grade 
                Protection (MGP) will not apply.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedDealer?.dealer_rate && (
              <Button 
                variant="outline"
                onClick={handleClearCustomRate}
                disabled={actionLoading}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="clear-custom-rate-btn"
              >
                Clear Custom Rate
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setCustomRateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSetCustomRate} 
              disabled={actionLoading || (customRateValue.trim() !== '' && (parseFloat(customRateValue) <= 0 || parseFloat(customRateValue) > 50))}
              className="bg-[#003594] hover:bg-[#002554]"
              data-testid="save-custom-rate-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete {deleteType === 'application' ? 'Application' : 'Item'}
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the{' '}
              {deleteType === 'application' ? (
                <>dealer application from <span className="font-bold text-gray-900">{itemToDelete?.name}</span> ({itemToDelete?.email})</>
              ) : (
                <>selected item</>
              )}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will permanently remove:
              </p>
              <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                {deleteType === 'application' && (
                  <>
                    <li>The dealer application record</li>
                    <li>Business information and goals submitted</li>
                  </>
                )}
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Note: The user account will not be affected by this action.
              </p>
            </div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm:
            </Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              disabled={actionLoading || deleteConfirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDealersPage;
